"use client"

import { useEffect } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Typography } from "@/shared/ui/atoms"
import { CustomButton } from "@/shared/ui/molecules"
import { useGetLocale } from "@/shared/hooks/useGetLocale"
import useIconAction from "@/shared/hooks/useIconAction"
import { reportFrontendError } from "@/shared/lib/frontend-error-reporter"

const ERROR_MESSAGES: Record<string, { title: string; description: string; button: string }> = {
  ko: {
    title: "오류가 발생했습니다",
    description: "페이지를 불러오는 중 문제가 발생했습니다",
    button: "이전으로",
  },
  en: {
    title: "An error occurred",
    description: "A problem occurred while loading the page",
    button: "Go back",
  },
  cn: {
    title: "发生错误",
    description: "加载页面时出现问题",
    button: "返回",
  },
  jp: {
    title: "エラーが発生しました",
    description: "ページの読み込み中に問題が発生しました",
    button: "戻る",
  },
}

export default function Error({ error }: { error: Error & { digest?: string } }) {
  if (process.env.NODE_ENV === "development") {
    console.error(error)
  }

  const queryClient = useQueryClient()
  const locale = useGetLocale() || "ko"
  const { handleBackRoute } = useIconAction()

  useEffect(() => {
    reportFrontendError(error, { source: "react-boundary", digest: error.digest })
  }, [error])

  const handleGoBack = () => {
    queryClient.clear()
    handleBackRoute()
  }

  const errorText = ERROR_MESSAGES[locale] || ERROR_MESSAGES.ko
  const title = errorText.title
  const description = errorText.description
  const buttonText = errorText.button

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4">
      <Typography size={20} className="mb-4 font-bold text-center">
        {title}
      </Typography>
      <Typography size={16} className="mb-8 text-center text-[#5A5C63]">
        {description}
      </Typography>
      <CustomButton onClick={handleGoBack} className="px-6 py-3 bg-[#6B00D7] rounded-lg">
        <Typography className="text-white font-medium">{buttonText}</Typography>
      </CustomButton>
    </div>
  )
}
